import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function PortfolioPage() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [cars, setCars] = useState([]);
  const [activeCar, setActiveCar] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCars();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [activeCar]);

  const fetchCars = async () => {
    try {
      const res = await axios.get(`${API}/portfolio/cars`);
      setCars(res.data);
    } catch (err) {
      console.error("Error fetching cars:", err);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = activeCar !== "all" ? { car_name: activeCar } : {};
      const res = await axios.get(`${API}/portfolio/events`, { params });
      setEvents(res.data);
    } catch (err) {
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  };

  // Group events by car_name
  const groupedByCar = events.reduce((acc, event) => {
    const car = event.car_name || t("portfolio.other");
    if (!acc[car]) acc[car] = [];
    acc[car].push(event);
    return acc;
  }, {});

  const openLightbox = (event, photoIndex) => {
    setSelectedEvent(event);
    setLightboxIndex(photoIndex);
  };

  const closeLightbox = () => {
    setSelectedEvent(null);
    setLightboxIndex(null);
  };

  const prevPhoto = () => {
    if (selectedEvent && lightboxIndex > 0) setLightboxIndex(lightboxIndex - 1);
  };

  const nextPhoto = () => {
    if (selectedEvent && lightboxIndex < selectedEvent.photos.length - 1) setLightboxIndex(lightboxIndex + 1);
  };

  return (
    <div data-testid="portfolio-page">
      <SEO titleKey="seo.portfolio_title" descriptionKey="seo.portfolio_description" path="/portfolio" />
      {/* Header */}
      <section className="pt-32 pb-12 lg:pt-40 lg:pb-16" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("portfolio.eyebrow")}
            </p>
          </motion.div>
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1B1A] max-w-3xl mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t("portfolio.title_1")} <span style={{ fontWeight: 600 }}>{t("portfolio.title_2")}</span>
          </motion.h1>

          {/* Car Filter */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="flex flex-wrap gap-3"
            data-testid="car-filters"
          >
            <button
              data-testid="filter-all"
              onClick={() => setActiveCar("all")}
              className={`text-xs uppercase tracking-[0.15em] px-6 py-3 border transition-all duration-300 ${
                activeCar === "all"
                  ? "bg-[#1A1B1A] text-[#FAF9F6] border-[#1A1B1A]"
                  : "bg-transparent text-[#4A4A4A] border-[#D8D4CC] hover:border-[#1A1B1A]"
              }`}
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
            >
              {t("portfolio.filter_all")}
            </button>
            {cars.map((car) => (
              <button
                key={car}
                data-testid={`filter-${car.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setActiveCar(car)}
                className={`text-xs uppercase tracking-[0.15em] px-6 py-3 border transition-all duration-300 ${
                  activeCar === car
                    ? "bg-[#1A1B1A] text-[#FAF9F6] border-[#1A1B1A]"
                    : "bg-transparent text-[#4A4A4A] border-[#D8D4CC] hover:border-[#1A1B1A]"
                }`}
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
              >
                {car}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Events by Car */}
      <section className="pb-24 lg:pb-32" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border border-[#C4A462] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <p className="text-center text-[#757575] py-20" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {t("portfolio.empty")}
            </p>
          ) : (
            <div className="space-y-32">
              {Object.entries(groupedByCar).map(([carName, carEvents]) => (
                <div key={carName} data-testid={`car-group-${carName.toLowerCase().replace(/\s+/g, '-')}`}>
                  {/* Car Name Header */}
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    className="mb-12"
                  >
                    <div className="luxury-divider mb-6" />
                    <h2
                      className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A]"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                    >
                      {carName}
                    </h2>
                  </motion.div>

                  {/* Events for this car */}
                  <div className="space-y-20 lg:space-y-24">
                    {carEvents.map((event, eventIndex) => (
                      <motion.div
                        key={event.id}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                        variants={fadeUp}
                        data-testid={`event-card-${event.id}`}
                      >
                        {/* Event Info */}
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-3">
                          <div>
                            <p
                              className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-2"
                              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                            >
                              {event.category} &mdash; {event.date}
                            </p>
                            <h3
                              className="text-xl sm:text-2xl tracking-tighter text-[#1A1B1A]"
                              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                            >
                              {event.title}
                            </h3>
                          </div>
                          <p
                            className="text-xs text-[#757575] uppercase tracking-[0.15em]"
                            style={{ fontFamily: "'DM Sans', sans-serif" }}
                          >
                            {event.location}
                          </p>
                        </div>

                        <p
                          className="text-sm text-[#4A4A4A] leading-relaxed max-w-2xl mb-6"
                          style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                        >
                          {event.description}
                        </p>

                        {/* Photo Grid */}
                        <div className="grid grid-cols-12 gap-3 lg:gap-4">
                          {event.photos.map((photo, photoIndex) => {
                            const spans = [
                              "col-span-12 lg:col-span-8",
                              "col-span-6 lg:col-span-4",
                              "col-span-6 lg:col-span-4",
                              "col-span-12 lg:col-span-8",
                            ];
                            const heights = [
                              "h-48 lg:h-80",
                              "h-48 lg:h-80",
                              "h-40 lg:h-64",
                              "h-40 lg:h-64",
                            ];
                            return (
                              <motion.div
                                key={photo.url}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeUp}
                                custom={photoIndex}
                                className={`${spans[photoIndex % 4]} img-hover-zoom cursor-pointer`}
                                onClick={() => openLightbox(event, photoIndex)}
                                data-testid={`photo-${event.id}-${photoIndex}`}
                              >
                                <img
                                  src={photo.url.startsWith("/api") ? `${BACKEND_URL}${photo.url}` : photo.url}
                                  alt={photo.caption || event.title}
                                  className={`w-full ${heights[photoIndex % 4]} object-cover`}
                                />
                              </motion.div>
                            );
                          })}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedEvent && lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-[#1A1B1A]/95 flex items-center justify-center"
            data-testid="lightbox"
            onClick={closeLightbox}
          >
            <button
              data-testid="lightbox-close"
              className="absolute top-6 right-6 text-[#FAF9F6]/70 hover:text-[#FAF9F6] transition-colors"
              onClick={closeLightbox}
            >
              <X strokeWidth={1.25} size={28} />
            </button>

            {lightboxIndex > 0 && (
              <button
                data-testid="lightbox-prev"
                className="absolute left-6 text-[#FAF9F6]/70 hover:text-[#FAF9F6] transition-colors"
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
              >
                <ChevronLeft strokeWidth={1.25} size={36} />
              </button>
            )}

            {lightboxIndex < selectedEvent.photos.length - 1 && (
              <button
                data-testid="lightbox-next"
                className="absolute right-6 text-[#FAF9F6]/70 hover:text-[#FAF9F6] transition-colors"
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
              >
                <ChevronRight strokeWidth={1.25} size={36} />
              </button>
            )}

            <div className="max-w-5xl max-h-[85vh] mx-6" onClick={(e) => e.stopPropagation()}>
              <img
                src={(() => {
                  const url = selectedEvent.photos[lightboxIndex].url;
                  return url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;
                })()}
                alt={selectedEvent.photos[lightboxIndex].caption || ""}
                className="max-h-[75vh] w-auto mx-auto object-contain"
                data-testid="lightbox-image"
              />
              {selectedEvent.photos[lightboxIndex].caption && (
                <p
                  className="text-center text-sm text-[#FAF9F6]/70 mt-4"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                >
                  {selectedEvent.photos[lightboxIndex].caption}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
